"use client";

import { useEffect } from "react";
import { useTransitionRouter } from "next-view-transitions";

/**
 * Capture-phase click interceptor for every internal `<a>`. The click is
 * preventDefault'd and routed through `useTransitionRouter().push` from
 * `next-view-transitions`, which:
 *   1. wraps the push in `React.startTransition`,
 *   2. opens a `document.startViewTransition` with a Promise callback that
 *      only resolves once the new route's component tree has mounted (the
 *      library's `<ViewTransitions>` provider blocks the new render with
 *      `use(...)` until that signal arrives).
 *
 * That ordering is the key — the browser captures the NEW snapshot after the
 * RSC stream has hydrated, not before. A naive `flushSync(router.push)` would
 * snapshot the old DOM (RSC not yet in) and the animation would play over two
 * identical frames, which reads as a flicker.
 *
 * Keeping the interceptor (rather than switching every `Link` to the lib's
 * `Link`) lets us preserve next-intl's locale-prefixing via `@/i18n/routing`.
 */
export function ViewTransitionRouter() {
  const router = useTransitionRouter();

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (typeof document.startViewTransition !== "function") return;

    function handleClick(e: MouseEvent) {
      if (e.defaultPrevented) return;
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const link = (e.target as HTMLElement | null)?.closest("a");
      if (!link) return;
      if (link.target && link.target !== "" && link.target !== "_self") return;
      if (link.hasAttribute("download")) return;

      const href = link.getAttribute("href");
      if (!href) return;
      if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      router.push(url.pathname + url.search + url.hash);
    }

    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [router]);

  return null;
}
